import React, { useState } from 'react';
import { useRoute, Link } from 'wouter';
import { useInvoices } from '../hooks/use-invoices';
import { useToast } from '../hooks/use-toast';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { formatCurrency } from '../lib/utils';

// UI Components
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import StatusBadge from '../components/shared/status-badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

// Icons
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Download, 
  Send, 
  DollarSign,
  Calendar,
  User,
  FileText,
  RefreshCw,
  MoreHorizontal,
  Check,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown
} from 'lucide-react';

// Layout Components
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';

// Helper function to format date - matches Estimates page format (8/25/2025)
const formatDate = (dateString?: string | Date | null) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return 'Invalid Date';
  }
};

// Helper function to get status badge styling (standardized)
const getStatusBadge = (status: string) => {
  return <StatusBadge status={status} type="invoice" />;
};

export default function InvoicesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>("date"); // ALWAYS sort by date
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc"); // Newest first
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { toast } = useToast();
  const { 
    invoices, 
    isLoadingInvoices, 
    deleteInvoiceMutation,
    refetchInvoices 
  } = useInvoices();

  // Filter and sort invoices - ALWAYS SORT BY DATE (NEWEST FIRST)
  const filteredInvoices = (invoices || [])
    .filter((invoice: any) => {
      const clientName = invoice.client ? `${invoice.client.firstName || ''} ${invoice.client.lastName || ''}`.trim() : '';
      const projectTitle = invoice.project?.title || '';
      
      const matchesSearch = 
        invoice.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        projectTitle.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || invoice.status?.toLowerCase() === statusFilter.toLowerCase();
      
      return matchesSearch && matchesStatus;
    })
    .sort((a: any, b: any) => {
      // ALWAYS SORT BY DATE - Use issue_date (conversion date) with created_at as fallback
      const aDate = new Date(a.issue_date || a.issueDate || a.created_at || a.createdAt).getTime();
      const bDate = new Date(b.issue_date || b.issueDate || b.created_at || b.createdAt).getTime();
      
      // Sort based on direction: desc = newest first, asc = oldest first
      return sortDirection === "desc" ? (bDate - aDate) : (aDate - bDate);
    });

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedInvoices = filteredInvoices.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Handle delete confirmation
  const handleDeleteClick = (invoice: any) => {
    setInvoiceToDelete(invoice);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (invoiceToDelete) {
      try {
        await deleteInvoiceMutation.mutateAsync(invoiceToDelete.id);
        setIsDeleteModalOpen(false);
        setInvoiceToDelete(null);
        toast({
          title: 'Invoice deleted',
          description: 'The invoice has been deleted successfully.',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to delete invoice.',
          variant: 'destructive',
        });
      }
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    refetchInvoices();
    toast({
      title: 'Data refreshed',
      description: 'Invoice data has been refreshed.',
    });
  };

  // Toggle date sort direction only (always sort by date)
  const toggleDateSort = () => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  };

  // Get date sort icon
  const getDateSortIcon = () => {
    return sortDirection === "desc" 
      ? <ChevronDown className="h-4 w-4 text-green-500" />
      : <ChevronUp className="h-4 w-4 text-green-500" />;
  };

  // Calculate summary stats
  const totalInvoices = filteredInvoices.length;
  const totalAmount = filteredInvoices.reduce((sum: number, invoice: any) => sum + (parseFloat(invoice.total) || 0), 0);
  const paidAmount = filteredInvoices
    .filter((invoice: any) => invoice.status?.toLowerCase() === 'paid')
    .reduce((sum: number, invoice: any) => sum + (parseFloat(invoice.total) || 0), 0);
  // Calculate pending amount as total unpaid balance across all invoices
  const pendingAmount = filteredInvoices.reduce((sum: number, invoice: any) => {
    const total = parseFloat(invoice.total) || 0;
    const paid = parseFloat(invoice.amountPaid) || 0;
    const remaining = total - paid;
    return sum + (remaining > 0 ? remaining : 0);
  }, 0);

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-4 md:p-6 pt-24 flex flex-col items-center">
            <div className="w-full space-y-3">
              <div>
                <h1 className="text-2xl font-bold text-[var(--dash-text)]">Invoices</h1>
                <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">Manage your invoices and payments.</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--dash-text-muted)]" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)] text-[var(--dash-text)] placeholder:text-[var(--dash-text-muted)]"
                  />
                </div>
                <Select value={statusFilter} onValueChange={(v: string) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-[140px] h-10 rounded-lg border-[var(--dash-border)] bg-[var(--dash-card)]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All status</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partially_paid">Partially Paid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button asChild className="h-10 rounded-lg bg-[var(--dash-accent)] hover:opacity-90 text-white ml-auto">
                  <Link href="/invoices/create">
                    <Plus className="h-4 w-4 mr-2" />
                    Create invoice
                  </Link>
                </Button>
                <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]" onClick={() => {
                  let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
                  csvContent += "Invoice Number,Client Name,Client Email,Service Type,Status,Total Amount,Amount Paid,Pending Amount,Issue Date,Due Date,Line Item Description,Quantity,Unit Price,Line Total\n";
                  (filteredInvoices as any[] || []).forEach((inv: any) => {
                    const invoiceNumber = inv.invoice_number || inv.invoiceNumber || 'N/A';
                    const clientName = `${inv.client?.firstName || ''} ${inv.client?.lastName || ''}`.trim() || 'Unknown';
                    const clientEmail = inv.client?.email || 'N/A';
                    const serviceType = inv.serviceType || inv.service_type || 'N/A';
                    const status = inv.status || 'N/A';
                    const total = parseFloat(inv.total || 0);
                    const amountPaid = parseFloat(inv.amountPaid || 0);
                    const pendingAmount = total - amountPaid;
                    const issueDate = inv.issue_date ? new Date(inv.issue_date).toLocaleDateString() : 'N/A';
                    const dueDate = inv.due_date ? new Date(inv.due_date).toLocaleDateString() : 'N/A';
                    if (inv.items && inv.items.length > 0) {
                      inv.items.forEach((item: any) => {
                        const description = (item.description || 'No description').replace(/"/g, '""');
                        const quantity = parseFloat(item.quantity || 0);
                        const unitPrice = parseFloat(item.unitPrice || item.unit_price || 0);
                        const lineTotal = parseFloat(item.amount || (quantity * unitPrice));
                        csvContent += `"${invoiceNumber}","${clientName}","${clientEmail}","${serviceType}","${status}","$${total.toFixed(2)}","$${amountPaid.toFixed(2)}","$${pendingAmount.toFixed(2)}","${issueDate}","${dueDate}","${description}","${quantity}","$${unitPrice.toFixed(2)}","$${lineTotal.toFixed(2)}"\n`;
                      });
                    } else {
                      csvContent += `"${invoiceNumber}","${clientName}","${clientEmail}","${serviceType}","${status}","$${total.toFixed(2)}","$${amountPaid.toFixed(2)}","$${pendingAmount.toFixed(2)}","${issueDate}","${dueDate}","No line items","","",""\n`;
                    }
                  });
                  const link = document.createElement("a");
                  link.setAttribute("href", encodeURI(csvContent));
                  link.setAttribute("download", "invoices_with_line_items.csv");
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  toast({ title: "Export Complete", description: "Invoices exported to CSV" });
                }}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
                <Button variant="outline" size="sm" className="h-10 rounded-lg border-[var(--dash-border)]" onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><FileText className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{totalInvoices}</span>
                    <span className="dash-stat-delta positive">+{(invoices || []).filter((inv: any) => new Date(inv.issue_date || inv.created_at || inv.createdAt).getMonth() === new Date().getMonth()).length}</span>
                  </div>
                  <div className="dash-stat-label">Total invoices</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><DollarSign className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{formatCurrency(totalAmount)}</span>
                  </div>
                  <div className="dash-stat-label">Total amount</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><Check className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{formatCurrency(paidAmount)}</span>
                  </div>
                  <div className="dash-stat-label">Paid</div>
                </div>
                <div className="dash-stat-card">
                  <div className="flex items-center gap-2 mb-1"><Calendar className="h-5 w-5 text-[var(--dash-text-muted)]" /></div>
                  <div className="flex items-baseline">
                    <span className="dash-stat-number">{formatCurrency(pendingAmount)}</span>
                  </div>
                  <div className="dash-stat-label">Pending</div>
                </div>
              </div>

            <div className="rounded-xl border border-[var(--dash-border)] bg-[var(--dash-card)] shadow-[var(--dash-shadow)] overflow-hidden">
                {isLoadingInvoices ? (
                  <div className="text-center py-12 text-[var(--dash-text-muted)]">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--dash-border)] border-t-[var(--dash-accent)] mx-auto"></div>
                    <p className="mt-2">Loading invoices…</p>
                  </div>
                ) : filteredInvoices.length === 0 ? (
                  <div className="text-center py-12 text-[var(--dash-text-muted)]">
                    <div className="text-4xl mb-4">📄</div>
                    <div className="text-lg font-medium text-[var(--dash-text)]">No invoices found</div>
                    <p className="mt-2">
                      {searchQuery || statusFilter !== 'all' ? 'No invoices match your filters.' : 'Start by creating your first invoice.'}
                    </p>
                    <Button className="mt-4" asChild>
                      <Link href="/invoices/create">
                        <Plus className="h-4 w-4 mr-2" />
                        Create first invoice
                      </Link>
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="hidden lg:block overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-[var(--dash-border)] hover:bg-transparent">
                            <TableHead className="text-[var(--dash-text-muted)] font-medium">Invoice #</TableHead>
                            <TableHead className="text-[var(--dash-text-muted)] font-medium">Client</TableHead>
                            <TableHead className="text-[var(--dash-text-muted)] font-medium">Project</TableHead>
                            <TableHead className="text-[var(--dash-text-muted)] font-medium">Amount</TableHead>
                            <TableHead className="text-[var(--dash-text-muted)] font-medium">Status</TableHead>
                            <TableHead
                              className="text-[var(--dash-text-muted)] font-medium cursor-pointer hover:bg-slate-50/80"
                              onClick={toggleDateSort}
                              title="Toggle sort"
                            >
                              <div className="flex items-center gap-1">
                                Date {sortDirection === 'desc' ? '(Newest)' : '(Oldest)'}
                                {getDateSortIcon()}
                              </div>
                            </TableHead>
                            <TableHead className="text-[var(--dash-text-muted)] font-medium text-right w-24">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedInvoices.map((invoice: any) => (
                            <TableRow key={invoice.id} className="border-[var(--dash-border)] hover:bg-slate-50/80">
                              <TableCell className="font-medium py-2">
                                <Link href={`/invoices/${invoice.id}`} className="text-[var(--dash-text)] hover:underline">
                                  {invoice.invoice_number}
                                </Link>
                              </TableCell>
                              <TableCell className="py-2 text-[var(--dash-text)]">
                                {invoice.client ? `${invoice.client.firstName || ''} ${invoice.client.lastName || ''}`.trim() || 'N/A' : 'N/A'}
                              </TableCell>
                              <TableCell className="py-2 text-[var(--dash-text)]">{invoice.project?.title || invoice.projectTitle || (invoice.project_id ? `Project #${invoice.project_id}` : '-')}</TableCell>
                              <TableCell className="font-medium py-2 text-[var(--dash-text)]">{formatCurrency(invoice.total)}</TableCell>
                              <TableCell className="py-2">{getStatusBadge(invoice.status)}</TableCell>
                              <TableCell className="py-2 text-[var(--dash-text)]">{formatDate(invoice.issue_date || invoice.issueDate || invoice.created_at || invoice.createdAt)}</TableCell>
                              <TableCell className="py-2 text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 border border-[var(--dash-border)] rounded-md">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="border-[var(--dash-border)] bg-[var(--dash-card)]">
                                    <DropdownMenuItem asChild>
                                      <Link href={`/invoices/${invoice.id}`}><Eye className="h-4 w-4 mr-2" />View</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                      <Link href={`/invoices/${invoice.id}`}><Edit className="h-4 w-4 mr-2" />Edit</Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeleteClick(invoice)} className="text-red-600">
                                      <Trash2 className="h-4 w-4 mr-2" />Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="lg:hidden space-y-4 p-4">
                      {paginatedInvoices.map((invoice: any) => (
                        <div key={invoice.id} className="rounded-lg border border-[var(--dash-border)] bg-[var(--dash-card)] p-4 space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <Link href={`/invoices/${invoice.id}`} className="font-medium text-[var(--dash-text)] hover:underline">
                                {invoice.invoice_number}
                              </Link>
                              <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">
                                {invoice.client ? `${invoice.client.firstName || ''} ${invoice.client.lastName || ''}`.trim() || 'N/A' : 'N/A'}
                              </p>
                            </div>
                            {getStatusBadge(invoice.status)}
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-[var(--dash-text-muted)]">Project</span>
                            <span className="text-[var(--dash-text)]">{invoice.project?.title || invoice.projectTitle || '-'}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-[var(--dash-text-muted)]">Amount</span>
                            <span className="font-medium text-[var(--dash-text)]">{formatCurrency(invoice.total)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-[var(--dash-text-muted)]">Date</span>
                            <span className="text-[var(--dash-text)]">{formatDate(invoice.issue_date || invoice.issueDate || invoice.created_at || invoice.createdAt)}</span>
                          </div>
                          <div className="pt-2 flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 border border-[var(--dash-border)]">
                                  <MoreHorizontal className="h-4 w-4 mr-1" />Actions
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="border-[var(--dash-border)]">
                                <DropdownMenuItem asChild><Link href={`/invoices/${invoice.id}`}><Eye className="h-4 w-4 mr-2" />View</Link></DropdownMenuItem>
                                <DropdownMenuItem asChild><Link href={`/invoices/${invoice.id}`}><Edit className="h-4 w-4 mr-2" />Edit</Link></DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteClick(invoice)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      ))}
                    </div>

                    {totalPages > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2 border-t border-[var(--dash-border)] bg-[var(--dash-bg)]">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-[var(--dash-text-muted)]">Rows per page</span>
                          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                            <SelectTrigger className="w-[72px] h-9 border-[var(--dash-border)]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[10, 25, 50, 100].map((n) => (
                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="text-sm text-[var(--dash-text-muted)]">
                          {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredInvoices.length)} of {filteredInvoices.length}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="sm" className="h-9 w-9 p-0 border-[var(--dash-border)]" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>←</Button>
                          {(() => {
                            const show = Math.min(5, totalPages);
                            let start = currentPage <= 2 ? 1 : Math.min(currentPage - 2, totalPages - show + 1);
                            start = Math.max(1, start);
                            const pages: number[] = [];
                            for (let i = 0; i < show; i++) pages.push(start + i);
                            return pages.map((p) => (
                              <Button key={p} variant={currentPage === p ? 'default' : 'outline'} size="sm" className="h-9 w-9 p-0 min-w-9" onClick={() => setPage(p)}>{p}</Button>
                            ));
                          })()}
                          <Button variant="outline" size="sm" className="h-9 w-9 p-0 border-[var(--dash-border)]" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>→</Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
            </div>
            </div>
          </main>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice "{invoiceToDelete?.invoice_number}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 